import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import Breadcrumb from "@/components/Breadcrumb";
import { useBlogPosts } from "@/lib/siteConfig";
import { EditableText } from "@/lib/editMode";

const formatDate = (s) => {
    try {
        return new Date(s).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return s;
    }
};

export default function Blog() {
    const posts = useBlogPosts();
    useSEO({
        title: "Articles — Virginia Car Laws, Pricing Trends, Mobile Mechanic Tips",
        description:
            "Articles on new Virginia car laws, inspection updates, parts price fluctuations, and Hampton Roads mobile mechanic tips. New article each month from Limit Break Performance.",
        image: posts[0]?.cover,
    });

    return (
        <div data-testid="blog-page">
            <Breadcrumb section="Articles" />
            <section className="border-b border-white/10 bg-black">
                <div className="lbp-container pt-8 pb-16 sm:pb-20 lg:pb-24">
                    <EditableText
                        path="blog.page.eyebrow"
                        defaultValue="New Virginia car laws · Pricing trends · Tips"
                        as="p"
                        className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                    />
                    <EditableText
                        path="blog.page.title"
                        defaultValue="Articles"
                        as="h1"
                        className="mt-2 font-display font-black uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl"
                        data-testid="blog-hero-title"
                    />
                    <EditableText
                        path="blog.page.subtitle"
                        defaultValue="New article every month — Virginia car laws, inspection updates, parts price moves, and the kind of stuff dealerships don't tell you."
                        multiline
                        as="p"
                        className="mt-4 max-w-2xl text-zinc-400 text-base sm:text-lg leading-relaxed"
                    />
                </div>
            </section>

            <section className="bg-zinc-950">
                <div className="lbp-container py-14 sm:py-20 space-y-5">
                    {posts.map((post, i) => (
                        <Link
                            to={`/articles/${post.slug}`}
                            key={post.slug}
                            data-testid={`blog-card-${i}`}
                            className="group grid md:grid-cols-5 gap-0 overflow-hidden rounded-sm border border-white/10 bg-zinc-900 hover:border-red-600/50 transition-colors"
                        >
                            <div className="md:col-span-2 relative aspect-[16/10] md:aspect-auto md:min-h-[260px]">
                                <img
                                    src={post.cover}
                                    alt={post.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    loading="lazy"
                                />
                            </div>
                            <div className="md:col-span-3 p-6 sm:p-8 flex flex-col justify-center">
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs uppercase tracking-widest text-zinc-500 font-semibold">
                                    <span className="inline-flex items-center gap-1.5">
                                        <Calendar className="w-3.5 h-3.5 text-red-500" />
                                        {formatDate(post.date)}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <Clock className="w-3.5 h-3.5 text-red-500" />
                                        {post.readTime}
                                    </span>
                                </div>
                                <EditableText
                                    path={`blog.${post.slug}.title`}
                                    defaultValue={post.title}
                                    as="h2"
                                    className="mt-3 font-display font-bold text-white text-2xl sm:text-3xl leading-tight tracking-tight group-hover:text-red-400 transition-colors"
                                />
                                <EditableText
                                    path={`blog.${post.slug}.excerpt`}
                                    defaultValue={post.excerpt}
                                    multiline
                                    as="p"
                                    className="mt-3 text-zinc-400 leading-relaxed"
                                />
                                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wider text-white">
                                    Read article <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
